import { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ShieldCheck, Bell, Wrench, ArrowRight, Phone, MapPin, Mail, Menu, X, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1772034736198-b194e4d72a1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TECH = "https://images.unsplash.com/photo-1748256086767-8974ee677f77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ALARM = "https://images.unsplash.com/photo-1666734057863-0087ebfa44a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export const WHATSAPP_NUMBER = "917005328781";
export const waLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
export const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35z"/>
    <path d="M20.52 3.48A11.87 11.87 0 0 0 12.04 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.3-1.65a11.9 11.9 0 0 0 5.74 1.46h.01c6.55 0 11.88-5.34 11.88-11.9 0-3.18-1.24-6.17-3.41-8.43zM12.05 21.8h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.9 6.99c0 5.45-4.44 9.89-9.9 9.89z"/>
  </svg>
);

export function PublicNav() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <header className="sticky top-0 z-50 bg-secondary/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white shrink-0" data-testid="brand-logo">
            <Flame className="text-primary shrink-0" size={26} />
            <span className="font-heading font-black text-sm sm:text-lg tracking-tight leading-none whitespace-nowrap">
              <span className="lg:hidden">SECUREFLAME</span>
              <span className="hidden lg:inline">SECUREFLAME FIRE SYSTEM PVT LTD</span>
            </span>
          </Link>
          <nav className="flex items-center gap-3 sm:gap-6">
            <Link to="/catalog" className="hidden sm:inline text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-catalog">Products</Link>
            <Link to="/login" className="hidden sm:inline text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-login">Login</Link>
            <Link to="/register"><Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90" data-testid="nav-register">Get Access</Button></Link>
            <button onClick={() => setOpen((v) => !v)} className="sm:hidden text-white p-1 -mr-1" aria-label="Menu" data-testid="mobile-menu-btn">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
        </div>
        {open && (
          <div className="sm:hidden absolute top-16 left-0 right-0 bg-secondary border-b border-white/10 px-4 py-2 flex flex-col animate-fade-up" data-testid="mobile-menu">
            <Link to="/catalog" onClick={() => setOpen(false)} className="py-3 text-slate-200 hover:text-white border-b border-white/5" data-testid="mobile-nav-catalog">Products</Link>
            <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-slate-200 hover:text-white" data-testid="mobile-nav-login">Login</Link>
          </div>
        )}
      </header>

      <a href={waLink("Hi Secureflame, I'd like to enquire about your fire safety products.")} target="_blank" rel="noreferrer" className="sm:hidden fixed bottom-20 right-5 z-50 flex items-center justify-center rounded-full bg-[#25D366] text-white w-14 h-14 shadow-lg shadow-green-600/30 active:scale-95 transition-transform" data-testid="mobile-whatsapp-fab" aria-label="Chat on WhatsApp">
        <WhatsAppIcon size={26} />
      </a>
      <Link to="/register" className="sm:hidden fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-primary text-white px-5 py-3 shadow-lg shadow-primary/30 active:scale-95 transition-transform" data-testid="mobile-quote-fab">
        <ClipboardList size={18} />
        <span className="font-semibold text-sm">Request a Quote</span>
      </Link>
    </>
  );
}

export function Footer() {
  return (
    <footer className="bg-secondary text-slate-400 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 text-white mb-3">
            <Flame className="text-primary" size={22} />
            <span className="font-heading font-black">SECUREFLAME FIRE SYSTEM PVT LTD</span>
          </div>
          <p className="text-sm max-w-md">Engineering safety since 2004. Certified fire protection products, installation and AMC services across India.</p>
        </div>
        <div className="text-sm space-y-2">
          <p className="flex items-center gap-2"><Phone size={15}/> +91 70053 28781, +91 92336 94924</p>
          <p className="flex items-center gap-2"><Mail size={15}/> info@secureflame.com</p>
          <p className="flex items-center gap-2"><MapPin size={15}/> Sujanpalli, Krishnanagar, Colonel Chowmuhani, Agartala, Tripura West</p>
        </div>
        <div className="text-sm">
          <p className="text-white font-semibold mb-2 uppercase text-xs tracking-widest">Portal</p>
          <Link to="/login" className="block hover:text-white">Client Login</Link>
          <Link to="/register" className="block hover:text-white">Register</Link>
          <Link to="/catalog" className="block hover:text-white">Catalog</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs">© 2026 Secureflame Fire System Pvt Ltd. All rights reserved.</div>
    </footer>
  );
}

const services = [
  { icon: Flame, title: "Fire Extinguishers", desc: "ABC, CO2 & water-mist extinguishers with certified refills and periodic servicing." },
  { icon: Bell, title: "Detection & Alarms", desc: "Addressable smoke detectors, control panels and manual call points." },
  { icon: ShieldCheck, title: "Suppression Systems", desc: "FM-200 clean agent, kitchen and sprinkler suppression systems." },
  { icon: Wrench, title: "AMC & Inspection", desc: "Scheduled on-site inspections, maintenance and compliance reporting." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative bg-secondary text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="fire safety" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-secondary/40" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-28 md:py-36">
          <p className="text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-5 animate-fade-up">Fire Protection · Engineered</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none max-w-4xl animate-fade-up" style={{ animationDelay: "0.1s" }}>
            PROTECT WHAT<br /><span className="text-primary">MATTERS MOST.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-xl animate-fade-up" style={{ animationDelay: "0.2s" }}>
            End-to-end fire safety systems, dealer ordering, and field-service inspection tracking — all in one enterprise portal.
          </p>
          <div className="mt-9 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/catalog"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="hero-browse-btn">Browse Catalog <ArrowRight size={18} /></Button></Link>
            <Link to="/register"><Button size="lg" variant="outline" className="rounded-sm border-white/30 text-white bg-transparent hover:bg-white/10" data-testid="hero-register-btn">Dealer Access</Button></Link>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">What we do</p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Complete fire safety, one partner.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
          {services.map((s, i) => (
            <div key={i} className="group p-8 border-r border-b border-border bg-card hover:bg-secondary transition-colors duration-200" data-testid={`service-${i}`}>
              <s.icon className="text-primary mb-5" size={34} />
              <h3 className="font-heading font-bold text-lg text-secondary group-hover:text-white transition-colors">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground group-hover:text-slate-300 transition-colors">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Split feature */}
      <section className="bg-muted">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img src={TECH} alt="technician inspection" className="w-full h-[420px] object-cover rounded-sm border border-border" />
          </div>
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">Field Service</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Inspection tracking that keeps you compliant.</h2>
            <p className="mt-5 text-muted-foreground">Technicians log on-site inspections, upload photos, record equipment status and set maintenance due-dates from any device. Clients get automatic email updates when a service is complete.</p>
            <ul className="mt-6 space-y-3 text-sm text-secondary">
              {["Mobile-friendly technician view", "Photo & report uploads", "Automated maintenance reminders", "PDF service records"].map((t) => (
                <li key={t} className="flex items-center gap-3"><ShieldCheck size={18} className="text-primary" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Our Installations */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">Our Work</p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Real installations across sites.</h2>
          <p className="mt-3 text-muted-foreground">Fire extinguishers, hydrant systems, pump rooms and alarm panels commissioned and maintained by our team on-site.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((n) => (
            <div key={n} className="aspect-square overflow-hidden rounded-sm border border-border bg-muted group" data-testid={`install-photo-${n}`}>
              <img src={`/installs/site-${n}.jpg`} alt={`Secureflame installation ${n}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">READY TO ORDER?</h2>
            <p className="mt-4 text-slate-300 max-w-md">Register as a client or dealer to place orders, download invoices and track your account ledger.</p>
            <Link to="/register"><Button size="lg" className="mt-8 rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="cta-register-btn">Create an account <ArrowRight size={18} /></Button></Link>
          </div>
          <img src={ALARM} alt="fire alarm" className="w-full h-72 object-cover rounded-sm border border-white/10" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
